import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Map, Bike, Car, MapPin, MessageCircle, Check, Minus, Plus, Package, BedDouble, Video } from "lucide-react";
import { toast } from "sonner";
import { api, waLink } from "@/lib/api";
import { photoSrc } from "@/components/Reviews";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const priceNum = (p) => Number(String(p || "").replace(/[^0-9]/g, "")) || 0;
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30);

function Step({ n, icon: Icon, title, hint, children, testId }) {
  return (
    <div className="bg-white border border-ink/10 rounded-2xl p-5 sm:p-6" data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-9 h-9 rounded-full bg-ink text-sand flex items-center justify-center font-mono text-xs">{n}</span>
        <div>
          <h3 className="font-serif text-lg font-bold text-ink flex items-center gap-2"><Icon size={16} className="text-terra" /> {title}</h3>
          <p className="text-xs text-ink/50">{hint}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Counter({ value, set, min, max, label, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <button type="button" onClick={() => set(Math.max(min, value - 1))} className="w-8 h-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-sand" data-testid={`${testId}-minus`}><Minus size={13} /></button>
      <span className="font-serif font-bold text-ink w-16 text-center text-sm" data-testid={`${testId}-value`}>{value} {label}</span>
      <button type="button" onClick={() => set(Math.min(max, value + 1))} className="w-8 h-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-sand" data-testid={`${testId}-plus`}><Plus size={13} /></button>
    </div>
  );
}

export default function PackageBuilder() {
  const [tours, setTours] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [places, setPlaces] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState("");
  const [roomIdx, setRoomIdx] = useState(0);
  const [nights, setNights] = useState(1);
  const [tourId, setTourId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [hours, setHours] = useState(24);
  const [days, setDays] = useState(1);
  const [cabKm, setCabKm] = useState(0);
  const [picked, setPicked] = useState([]);
  const [addons, setAddons] = useState([]);
  const [drone, setDrone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", travel_dates: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/places").then((r) => setPlaces(r.data)).catch(() => {});
    api.get("/hotels").then((r) => setHotels(r.data.filter((h) => h.tier === "budget"))).catch(() => {});
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
  }, []);

  const tour = tours.find((t) => t.id === tourId);
  const vehicle = vehicles.find((v) => v.id === vehicleId);
  const hotel = hotels.find((h) => h.id === hotelId);
  const room = hotel?.rooms?.[roomIdx] || hotel?.rooms?.[0];
  const cabRate = cabKm > 50 ? 20 : 75;
  const droneAddon = addons.find((a) => a.id === "drone");

  const lines = useMemo(() => {
    const out = [];
    if (tour) out.push({ label: tour.title, sub: tour.duration, amount: priceNum(tour.price) });
    if (vehicle) out.push({ label: vehicle.name, sub: `${hours} hr × ${days} day${days > 1 ? "s" : ""}`, amount: (hours === 12 ? vehicle.rate_12hr : vehicle.rate_24hr) * days });
    if (cabKm > 0) out.push({ label: `Car with driver · ${cabKm} km`, sub: `${cabKm > 50 ? "Outstation" : "Local"} @ ₹${cabRate}/km`, amount: cabKm * cabRate });
    if (hotel && room) out.push({ label: `Stay · ${hotel.name}`, sub: `${room.type} × ${nights} night${nights > 1 ? "s" : ""}`, amount: Number(room.price) * nights });
    if (drone && droneAddon) out.push({ label: droneAddon.name, sub: `${droneAddon.videos} aerial clips · ${droneAddon.minutes} min total`, amount: Number(droneAddon.price) });
    return out;
  }, [tour, vehicle, hours, days, cabKm, cabRate, hotel, room, nights, drone, droneAddon]);
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const pickedNames = places.filter((p) => picked.includes(p.id)).map((p) => p.name);

  const togglePlace = (id) => setPicked((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  const submit = async () => {
    if (lines.length === 0) return toast.error("Add at least one item — a tour, a rental or a cab");
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Add your name and WhatsApp number");
    setBusy(true);
    const breakdown = lines.map((l) => `• ${l.label} (${l.sub}) — ${rupees(l.amount)}`).join("\n");
    const stops = pickedNames.length ? `\nMust-see stops: ${pickedNames.join(", ")}` : "";
    const message = `Custom package quote:\n${breakdown}${stops}\nEstimated total: ${rupees(total)}${form.travel_dates ? `\nDates: ${form.travel_dates}` : ""}`;
    try {
      await api.post("/enquiries", {
        name: form.name, phone: form.phone, service: "Custom Package", travel_dates: form.travel_dates,
        message, kind: "package", estimate: total, vehicle_id: vehicle?.id || "", vehicle_name: vehicle?.name || "",
        duration_hours: vehicle ? hours : 0, days: vehicle ? days : 1,
      });
      toast.success("Quote saved! Opening WhatsApp so we can confirm it together.");
      window.open(waLink(`Hi Random Rider! I built a package on your site.\n${message}\nName: ${form.name}`), "_blank");
    } catch {
      toast.error("Could not save the quote. Please try WhatsApp directly.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

  return (
    <section id="package" className="py-20 lg:py-28" data-testid="package-builder-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">07 · Build Your Own Package</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
            Tour + ride + cab. <span className="italic text-terra">One quote.</span>
          </h2>
          <p className="mt-4 text-ink/70 leading-relaxed">Bundle what you want, see the estimate live, and send it to us on WhatsApp in one tap.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7 space-y-5">
            <Step n="1" icon={Map} title="Pick a tour" hint="Optional — local circuit or an Odisha package" testId="package-step-tour">
              <div className="grid sm:grid-cols-2 gap-2">
                {tours.map((t) => (
                  <button key={t.id} type="button" onClick={() => setTourId(tourId === t.id ? "" : t.id)} data-testid={`package-tour-${slugify(t.title)}`}
                    className={`text-left rounded-xl border px-3.5 py-3 transition-colors ${tourId === t.id ? "border-terra bg-terra/10" : "border-ink/10 hover:border-terra/40"}`}>
                    <p className="text-sm font-semibold text-ink leading-snug">{t.title}</p>
                    <p className="text-[11px] text-ink/50 mt-0.5">{t.duration} · <span className="text-terra font-semibold">{t.price}</span></p>
                  </button>
                ))}
              </div>
            </Step>

            <Step n="2" icon={Bike} title="Add a two-wheeler" hint="Optional — explore Puri on your own" testId="package-step-rental">
              <div className="grid sm:grid-cols-3 gap-2 mb-4">
                {vehicles.map((v) => (
                  <button key={v.id} type="button" onClick={() => setVehicleId(vehicleId === v.id ? "" : v.id)} data-testid={`package-vehicle-${slugify(v.name)}`}
                    className={`text-left rounded-xl border px-3.5 py-3 transition-colors ${vehicleId === v.id ? "border-terra bg-terra/10" : "border-ink/10 hover:border-terra/40"}`}>
                    <p className="text-sm font-semibold text-ink leading-snug">{v.name}</p>
                    <p className="text-[11px] text-ink/50 mt-0.5">₹{v.rate_12hr}/12hr · ₹{v.rate_24hr}/24hr</p>
                  </button>
                ))}
              </div>
              {vehicle && (
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex gap-2" data-testid="package-rental-hours">
                    {[12, 24].map((h) => (
                      <button key={h} type="button" onClick={() => setHours(h)} data-testid={`package-rental-${h}hr`}
                        className={`text-xs font-semibold px-3.5 py-2 rounded-full ${hours === h ? "bg-ink text-sand" : "bg-sand border border-ink/15 text-ink/60"}`}>{h} hr / day</button>
                    ))}
                  </div>
                  <Counter value={days} set={setDays} min={1} max={30} label={days > 1 ? "days" : "day"} testId="package-rental-days" />
                </div>
              )}
            </Step>

            <Step n="3" icon={Car} title="Need a car with driver?" hint="₹75/km up to 50 km · ₹20/km beyond · tolls, parking, night halt & driver food at actuals" testId="package-step-cab">
              <input type="range" min="0" max="600" step="10" value={cabKm} onChange={(e) => setCabKm(Number(e.target.value))} data-testid="package-cab-km-input" className="w-full accent-[#D95D39]" />
              <div className="mt-2 flex justify-between text-xs font-mono text-ink/50">
                <span>No car</span>
                <span className="text-ink font-semibold" data-testid="package-cab-km-value">{cabKm === 0 ? "No car needed" : `${cabKm} km · ${rupees(cabKm * cabRate)}`}</span>
                <span>600 km</span>
              </div>
            </Step>

            <Step n="4" icon={BedDouble} title="Add a budget stay? (optional)" hint="Low-cost partner rooms on the quiet beach side behind Puri Lighthouse" testId="package-step-stay">
              <div className="grid sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => setHotelId("")} data-testid="package-stay-none"
                  className={`text-left rounded-2xl border p-4 transition-colors ${!hotelId ? "border-ink bg-ink text-sand" : "border-ink/15 hover:border-ink/40"}`}>
                  <p className="font-semibold text-sm">No stay needed</p><p className={`text-xs mt-0.5 ${!hotelId ? "text-sand/70" : "text-ink/50"}`}>I'll arrange my own hotel</p>
                </button>
                {hotels.map((h) => (
                  <button key={h.id} type="button" onClick={() => { setHotelId(h.id); setRoomIdx(0); }} data-testid={`package-stay-${h.id.slice(0, 8)}`}
                    className={`text-left rounded-2xl border p-4 transition-colors ${hotelId === h.id ? "border-ink bg-ink text-sand" : "border-ink/15 hover:border-ink/40"}`}>
                    <p className="font-semibold text-sm">{h.name} <span className="font-normal opacity-70">· {h.area}</span></p>
                    <p className={`text-xs mt-0.5 ${hotelId === h.id ? "text-sand/70" : "text-ink/50"}`}>from {rupees(Math.min(...h.rooms.map((r) => Number(r.price))))}/night · {h.near}</p>
                  </button>
                ))}
              </div>
              {hotel && (
                <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="package-stay-options">
                  <select value={roomIdx} onChange={(e) => setRoomIdx(Number(e.target.value))} data-testid="package-stay-room" className="bg-sand border border-ink/15 rounded-full px-3.5 py-2 text-sm focus:outline-none">
                    {hotel.rooms.map((r, i) => <option key={r.type} value={i}>{r.type} · {rupees(Number(r.price))}/night</option>)}
                  </select>
                  <Counter value={nights} set={setNights} min={1} max={14} label={nights > 1 ? "nights" : "night"} testId="package-stay-nights" />
                  {hotel.maps_url && <a href={hotel.maps_url} target="_blank" rel="noreferrer" className="text-xs text-terra font-semibold underline" data-testid="package-stay-map">See on map</a>}
                  {Number(hotel.advance) > 0 && <p className="text-xs text-ink/60 mt-1" data-testid="package-stay-advance">₹{Number(hotel.advance).toLocaleString("en-IN")} advance confirms the room · balance at check-in</p>}
                </div>
              )}
            </Step>

            {droneAddon && (
              <Step n="5" icon={Video} title="Sweet memories from the sky? (optional)" hint={droneAddon.conditions} testId="package-step-drone">
                <button type="button" onClick={() => setDrone((d) => !d)} aria-pressed={drone} data-testid="package-drone-toggle"
                  className={`w-full text-left rounded-2xl border p-4 flex items-center gap-4 transition-colors ${drone ? "border-ink bg-ink text-sand" : "border-ink/15 hover:border-ink/40"}`}>
                  <img src={photoSrc(droneAddon.images[0])} alt="" className="w-20 h-14 rounded-lg object-cover shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm">{droneAddon.name} · {droneAddon.videos} clips · {droneAddon.minutes} min total</span>
                    <span className={`block text-xs mt-0.5 ${drone ? "text-sand/70" : "text-ink/50"}`}>{droneAddon.tagline}</span>
                  </span>
                  <span className="font-serif font-bold shrink-0">{drone ? <Check size={18} className="text-gold" /> : rupees(Number(droneAddon.price))}</span>
                </button>
              </Step>
            )}

            <Step n={droneAddon ? "6" : "5"} icon={MapPin} title="Must-see stops" hint="Tell us which sites matter most — we'll route around them" testId="package-step-places">
              <div className="flex flex-wrap gap-2">
                {places.map((p) => {
                  const on = picked.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => togglePlace(p.id)} data-testid={`package-place-${slugify(p.name)}`}
                      className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-terra text-sand border-terra" : "bg-sand border-ink/15 text-ink/70 hover:border-terra/50"}`}>
                      {on && <Check size={11} />} {p.name}
                    </button>
                  );
                })}
              </div>
            </Step>
          </div>

          <motion.aside initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-24 self-start bg-white border border-ink/10 shadow-lg shadow-ink/5 text-ink rounded-3xl p-6 sm:p-8 relative overflow-hidden" data-testid="package-summary">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-terra/10 blur-3xl" />
            <p className="font-mono text-[11px] uppercase tracking-widest text-terra flex items-center gap-2"><Package size={13} /> Your quote</p>
            <div className="mt-4 space-y-3 min-h-[80px]" data-testid="package-lines">
              {lines.length === 0 && <p className="text-sm text-ink/50">Nothing added yet — pick a tour, a ride or a cab.</p>}
              {lines.map((l) => (
                <div key={l.label} className="flex justify-between gap-3 text-sm border-b border-ink/10 pb-2">
                  <div><p className="font-medium">{l.label}</p><p className="text-[11px] text-ink/50">{l.sub}</p></div>
                  <p className="font-serif font-bold shrink-0">{rupees(l.amount)}</p>
                </div>
              ))}
              {pickedNames.length > 0 && <p className="text-[11px] text-ink/60"><span className="text-terra">Stops:</span> {pickedNames.join(", ")}</p>}
            </div>
            <div className="mt-5 flex items-end justify-between">
              <p className="text-xs font-mono uppercase tracking-widest text-ink/50">Estimated total</p>
              <motion.p key={total} initial={{ scale: 0.9, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} className="font-serif text-4xl font-black text-terra" data-testid="package-total">{rupees(total)}</motion.p>
            </div>
            <p className="text-[11px] text-ink/50 mt-1">Indicative estimate. Final quote confirmed on WhatsApp.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={`${inputCls} text-ink`} data-testid="package-name-input" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="WhatsApp number" className={`${inputCls} text-ink`} data-testid="package-phone-input" />
              <input value={form.travel_dates} onChange={(e) => setForm({ ...form, travel_dates: e.target.value })} placeholder="Travel dates (e.g. 12–15 Dec)" className={`${inputCls} text-ink col-span-2`} data-testid="package-dates-input" />
            </div>
            <button onClick={submit} disabled={busy} data-testid="package-submit-button"
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all disabled:opacity-60">
              <MessageCircle size={16} /> {busy ? "Saving..." : "Send quote on WhatsApp"}
            </button>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
